import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Check, Sparkles, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CARD_W = 360;
const GAP = 16;

function findTarget(targets) {
  if (!targets || !targets.length) return null;
  if (window.innerWidth < 768) return null; // mobile -> centered card
  for (const t of targets) {
    const el = document.querySelector(`[data-testid="${t}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && el.offsetParent !== null) return el;
    }
  }
  return null;
}

export default function OnboardingWizard({ hold = false, onDone }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null); // null => centered
  const startedRef = useRef(false);
  const doneRef = useRef(false);
  const signalDone = useCallback(() => {
    if (!doneRef.current) { doneRef.current = true; onDone?.(); }
  }, [onDone]);

  const steps = data?.steps || [];
  const step = steps[idx];
  const total = steps.length;

  // Fetch onboarding plan once user is available and the product intro has closed.
  useEffect(() => {
    if (!user || user.is_impersonating || hold) return;
    api.get("/onboarding/me").then((r) => {
      setData(r.data);
      if (r.data.should_show && r.data.steps?.length) setActive(true);
      else signalDone();
    }).catch(() => signalDone());
  }, [user, hold, signalDone]);

  const sendEvent = useCallback((event, extra = {}) => {
    api.post("/onboarding/event", { event, total_steps: total, ...extra }).catch(() => {});
  }, [total]);

  // Mark started.
  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true;
      sendEvent("started", { step_key: steps[0]?.key, step_index: 0 });
    }
  }, [active, steps, sendEvent]);

  const measure = useCallback(() => {
    if (!step) return;
    const el = findTarget(step.targets);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
  }, [step]);

  // Re-measure on step change, resize and scroll.
  useEffect(() => {
    if (!active || !step) return;
    sendEvent("step_viewed", { step_key: step.key, step_index: idx });
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 360); // after smooth scroll settles
    const onChange = () => measure();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [active, step, idx, measure, sendEvent]);

  if (!active || !step) return null;

  const isFirst = idx === 0;
  const isLast = idx === total - 1;

  const close = (event) => {
    setActive(false);
    if (event === "completed") sendEvent("completed", { step_key: step.key, step_index: idx });
    else sendEvent("abandoned", { step_key: step.key, step_index: idx });
    refresh();
    signalDone();
  };
  const next = () => { if (isLast) close("completed"); else setIdx((i) => i + 1); };
  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const goTo = (to) => { close("completed"); navigate(to); };

  // Card position.
  let cardStyle = { position: "fixed", width: CARD_W, maxWidth: "calc(100vw - 32px)", zIndex: 1002 };
  if (rect) {
    let left = rect.left + rect.width + GAP;
    if (left + CARD_W > window.innerWidth - 16) left = Math.max(16, rect.left - CARD_W - GAP);
    let top = Math.min(Math.max(16, rect.top), window.innerHeight - 300);
    cardStyle = { ...cardStyle, top, left };
  } else {
    cardStyle = { ...cardStyle, top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  return (
    <div className="onboarding-root" data-testid="onboarding-wizard">
      {/* Backdrop / spotlight */}
      {rect ? (
        <>
          <div className="fixed inset-0 z-[1000]" onClick={(e) => e.stopPropagation()} />
          <div
            data-testid="onboarding-spotlight"
            className="pointer-events-none fixed z-[1001] rounded-xl ring-2 ring-[#0F766E] transition-all duration-300 ease-out"
            style={{
              top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
              boxShadow: "0 0 0 9999px rgba(13,24,23,0.66)",
            }}
          />
        </>
      ) : (
        <div className="fixed inset-0 z-[1000] bg-[rgba(13,24,23,0.66)]" onClick={(e) => e.stopPropagation()} />
      )}

      {/* Focus bubble / card */}
      <div
        data-testid="onboarding-card"
        style={cardStyle}
        className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-2xl animate-[fadeIn_.25s_ease-out]"
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#0F766E]">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#0F766E]/10">
              <Sparkles className="h-4 w-4" strokeWidth={1.8} />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide">
              Step {idx + 1} of {total}
            </span>
          </div>
          <button
            data-testid="onboarding-skip-btn"
            onClick={() => close("abandoned")}
            className="rounded-lg p-1.5 text-[#9aa3a0] transition-colors hover:bg-[#f0f1ee] hover:text-[#636E72]"
            title="Skip the tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="font-display text-lg font-semibold text-[#2D3436]">{step.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-[#636E72]">{step.body}</p>

        {step.links?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {step.links.map((l) => (
              <a
                key={l.to}
                data-testid={`onboarding-link-${l.to.replace(/\W+/g, "-")}`}
                href={l.to}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium text-[#0F766E] transition-colors hover:bg-[#0F766E]/5"
              >
                {l.label} <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        )}

        {isLast && data?.quick_actions?.length > 0 && (
          <div className="mt-4 space-y-2">
            {data.quick_actions.map((a) => (
              <button
                key={a.to + a.label}
                data-testid={`onboarding-action-${a.label.replace(/\W+/g, "-").toLowerCase()}`}
                onClick={() => goTo(a.to)}
                className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 ${
                  a.primary
                    ? "bg-[#0F766E] text-white hover:shadow-lg"
                    : "border border-[#E2E8F0] text-[#2D3436] hover:bg-[#F4F6F2]"
                }`}
              >
                {a.label} <ArrowRight className="h-4 w-4" />
              </button>
            ))}
          </div>
        )}

        {/* Progress dots */}
        <div className="mt-4 flex items-center justify-center gap-1.5">
          {steps.map((s, i) => (
            <span
              key={s.key}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-5 bg-[#0F766E]" : i < idx ? "w-1.5 bg-[#0F766E]/40" : "w-1.5 bg-[#E2E8F0]"
              }`}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            data-testid="onboarding-prev-btn"
            onClick={prev}
            disabled={isFirst}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-[#636E72] transition-colors hover:bg-[#f0f1ee] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Previous
          </button>
          <div className="flex items-center gap-2">
            {!isLast && (
              <button
                data-testid="onboarding-skip-tour-btn"
                onClick={() => close("abandoned")}
                className="rounded-xl px-3 py-2 text-sm font-medium text-[#9aa3a0] transition-colors hover:text-[#636E72]"
              >
                Skip
              </button>
            )}
            <button
              data-testid={isLast ? "onboarding-finish-btn" : "onboarding-next-btn"}
              onClick={next}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#0F766E] px-4 py-2 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 hover:shadow-lg"
            >
              {isLast ? (<><Check className="h-4 w-4" /> Finish</>) : (<>Next <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
